import { useState } from "react";
import { FONT, T, PETROL } from "../theme";

// ── ARTICLE RESEARCH — REAL PAGE SCREENSHOTS (Dev2, 2026-07-14) ──────────────
// Erez wants the ACTUAL article pages on the board (like the website wall),
// not text-link boxes. Each article = an `articleShot` node: a clickable title
// header (opens the real source) sitting above the real captured screenshot.
// Grouped into per-company columns by band5-articles.ts. Screenshots captured
// with Playwright (1280x2000 @2x), hosted on R2.
export type ArticleShotData = { title: string; url: string; img: string; w: number; domain: string };
export type ArticleColHeaderData = { company: string; tag: string; blurb: string; count: number; w: number };

// per-column company header — sits atop each publisher's stack of real article shots
export function ArticleColHeaderNode({ data }: { data: ArticleColHeaderData }) {
  return (
    <div style={{ fontFamily: FONT, width: data.w }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 34, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em" }}>{data.company}</span>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", color: "#2B5560", background: "rgba(43,85,96,0.10)", borderRadius: 999, padding: "5px 12px" }}>{data.count} articles</span>
      </div>
      <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: PETROL }}>{data.tag}</div>
      <div style={{ marginTop: 10, fontSize: 15, color: T.ink2, lineHeight: 1.5 }}>{data.blurb}</div>
    </div>
  );
}

export function ArticleShotNode({ data }: { data: ArticleShotData }) {
  const [hover, setHover] = useState(false);
  return (
    <div style={{ fontFamily: FONT, width: data.w }}>
      {/* clickable title header — the whole bar links to the real article */}
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: "block",
          textDecoration: "none",
          pointerEvents: "auto",
          cursor: "pointer",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          border: `1px solid ${T.line}`,
          borderBottom: "none",
          background: hover ? "#FBF9F5" : T.white,
          padding: "13px 16px 12px",
        }}
      >
        <div style={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.3, color: PETROL, textDecoration: hover ? "underline" : "none", textUnderlineOffset: 3 }}>
          {data.title} ↗
        </div>
        <div style={{ marginTop: 4, fontSize: 11.5, color: "#9A9188", fontFamily: "ui-monospace, monospace" }}>{data.domain}</div>
      </a>
      {/* the real captured article page */}
      <div
        style={{
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          overflow: "hidden",
          border: `1px solid ${T.line}`,
          background: "#faf7f2",
          boxShadow: "0 20px 50px rgba(0,0,0,0.16)",
          fontSize: 0,
          lineHeight: 0,
        }}
      >
        <img src={data.img} alt={data.title} draggable={false} style={{ width: data.w, height: 640, objectFit: "cover", objectPosition: "top", display: "block" }} />
      </div>
    </div>
  );
}
